// src/components/Tracking.jsx

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

export default function Tracking() {
  const location = useLocation();
  const pingIntervalRef = useRef(null);

  const API_BASE = "https://reanimeitor.com/backend/tracking";

  const VISIT_INTERVAL_MS = 15 * 60 * 1000; // 15 minutos por URL
  const PING_INTERVAL_MS = 10 * 60 * 1000; // 10 minutos
  const MIN_PING_INTERVAL_MS = 10 * 60 * 1000; // 10 minutos globales entre pings

  const getOrCreateSessionId = () => {
    const storageKey = "reanimeitor_tracking_session_id";
    let sessionId = localStorage.getItem(storageKey);

    if (!sessionId) {
      sessionId = `rea_${Date.now()}_${Math.random().toString(36).substring(2, 14)}`;
      localStorage.setItem(storageKey, sessionId);
    }

    return sessionId;
  };

  const getUserData = () => {
    try {
      const rawUser = localStorage.getItem("userData");

      if (!rawUser) {
        return null;
      }

      const parsedUser = JSON.parse(rawUser);

      if (!parsedUser || !parsedUser.id) {
        return null;
      }

      return parsedUser;
    } catch (error) {
      return null;
    }
  };

  const getDeviceType = () => {
    const width = window.innerWidth;

    if (width <= 768) {
      return "mobile";
    }

    if (width <= 1024) {
      return "tablet";
    }

    return "desktop";
  };

  const getBrowserName = () => {
    const userAgent = navigator.userAgent || "";

    if (userAgent.includes("Edg")) {
      return "Edge";
    }

    if (userAgent.includes("Chrome")) {
      return "Chrome";
    }

    if (userAgent.includes("Firefox")) {
      return "Firefox";
    }

    if (userAgent.includes("Safari")) {
      return "Safari";
    }

    return "Unknown";
  };

  const simpleHash = (value) => {
    let hash = 0;
    const text = String(value || "");

    for (let i = 0; i < text.length; i += 1) {
      hash = ((hash << 5) - hash) + text.charCodeAt(i);
      hash |= 0;
    }

    return Math.abs(hash).toString(36);
  };

  const canSendByTime = (storageKey, intervalMs) => {
    try {
      const now = Date.now();
      const last = Number(localStorage.getItem(storageKey) || 0);

      if (last && now - last < intervalMs) {
        return false;
      }

      localStorage.setItem(storageKey, String(now));
      return true;
    } catch (error) {
      return true;
    }
  };

  const buildTrackingPayload = () => {
    const userData = getUserData();

    return {
      session_id: getOrCreateSessionId(),
      user_id: userData?.id || null,
      url: window.location.href,
      page_title: document.title || "",
      referrer: document.referrer || "",
      device: getDeviceType(),
      browser: getBrowserName()
    };
  };

  const registerVisit = async () => {
    const urlKey = simpleHash(`${location.pathname}${location.search}`);
    const storageKey = `reanimeitor_tracking_last_visit_${urlKey}`;

    if (!canSendByTime(storageKey, VISIT_INTERVAL_MS)) {
      return;
    }

    try {
      await axios.post(`${API_BASE}/visit.php`, buildTrackingPayload(), {
        headers: {
          "Content-Type": "application/json"
        },
        withCredentials: true,
        timeout: 8000
      });
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Tracking visit error:", error);
      }
    }
  };

  const registerPing = async () => {
    if (document.visibilityState === "hidden") {
      return;
    }

    const storageKey = "reanimeitor_tracking_last_ping_global";

    if (!canSendByTime(storageKey, MIN_PING_INTERVAL_MS)) {
      return;
    }

    try {
      await axios.post(`${API_BASE}/ping.php`, buildTrackingPayload(), {
        headers: {
          "Content-Type": "application/json"
        },
        withCredentials: true,
        timeout: 8000
      });
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Tracking ping error:", error);
      }
    }
  };

  useEffect(() => {
    registerVisit();
    registerPing();

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }

    pingIntervalRef.current = setInterval(() => {
      registerPing();
    }, PING_INTERVAL_MS);

    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [location.pathname, location.search]);

  return null;
}